import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { Refresh, TouchApp, DragIndicator, PictureAsPdf } from '@mui/icons-material'
import type { SessionFile } from '@fowix/types'
import { useStore } from '../store/useStore'
import { getSessionFiles } from '../utils/api'
import { ImageGrid } from '../components/ImageGrid'
import { ActionButtons } from '../components/ActionButtons'
import { CountdownTimer } from '../components/CountdownTimer'
import { AppHeader } from '../components/AppHeader'
import { AppFooter } from '../components/AppFooter'

const shellSx = { minHeight: '100vh', display: 'flex', flexDirection: 'column' as const }
const mainSx = { flex: 1 }

export const SessionView = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const { setSessionId, setFiles, files, expiresAt } = useStore()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchFiles = useCallback(async () => {
    if (!sessionId) return
    try {
      setLoading(true)
      setError(null)
      setSessionId(sessionId)
      const files = await getSessionFiles(sessionId)
      const enhancedFiles = files.map((file: SessionFile) => ({
        ...file,
        selected: false,
        order: null,
      }))
      setFiles(enhancedFiles)
    } catch (err) {
      console.error('Error fetching files:', err)
      setError(
        err instanceof Error && err.message.includes('404')
          ? 'This session has expired or is unknown. Start a new one from WhatsApp.'
          : 'We could not load your images. Check your connection and try again.'
      )
    } finally {
      setLoading(false)
    }
  }, [sessionId, setFiles, setSessionId])

  useEffect(() => {
    fetchFiles()
  }, [fetchFiles])

  if (loading) {
    return (
      <Box sx={shellSx}>
        <AppHeader />
        <Box component="main" sx={mainSx}>
          <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
            <Stack direction="row" alignItems="center" gap={1.5} mb={3}>
              <CircularProgress size={20} sx={{ color: 'primary.main' }} />
              <Typography variant="body1" color="text.secondary">
                Loading your WhatsApp images…
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: 2,
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" sx={{ aspectRatio: '1' }} />
              ))}
            </Box>
          </Container>
        </Box>
        <AppFooter />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={shellSx}>
        <AppHeader />
        <Box component="main" sx={mainSx}>
          <Container maxWidth="sm" sx={{ py: { xs: 6, sm: 10 } }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchFiles}
              fullWidth
            >
              Try again
            </Button>
          </Container>
        </Box>
        <AppFooter />
      </Box>
    )
  }

  return (
    <Box sx={shellSx}>
      <AppHeader />
      <Box component="main" sx={mainSx}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
            mb={1}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{ color: '#1E2A3B', fontWeight: 700, lineHeight: 1.2 }}
            >
              Pick the images, set the order, get your PDF.
            </Typography>
            {expiresAt && <CountdownTimer expiresAt={expiresAt} />}
          </Stack>

          <Typography variant="body1" color="text.secondary" mb={3}>
            {files.length > 0
              ? `${files.length} image${files.length > 1 ? 's' : ''} from your WhatsApp session, ready to assemble.`
              : 'Your WhatsApp session is open — images you forward will appear here.'}
          </Typography>

          {files.length > 0 && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'primary.light',
                border: '1px solid',
                borderColor: 'rgba(64, 192, 87, 0.25)',
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TouchApp fontSize="small" sx={{ color: 'primary.dark', mt: '2px' }} />
                <Typography variant="body2" sx={{ color: '#1E2A3B' }}>
                  <strong>Tap</strong> to select pages (a number shows the order).
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <DragIndicator fontSize="small" sx={{ color: 'primary.dark', mt: '2px' }} />
                <Typography variant="body2" sx={{ color: '#1E2A3B' }}>
                  <strong>Drag</strong> to reorder.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <PictureAsPdf fontSize="small" sx={{ color: 'primary.dark', mt: '2px' }} />
                <Typography variant="body2" sx={{ color: '#1E2A3B' }}>
                  <strong>Download or email</strong> when you're done.
                </Typography>
              </Stack>
            </Stack>
          )}

          <ImageGrid />
          {files.length === 0 && (
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchFiles}
              sx={{ mt: 2 }}
            >
              Refresh
            </Button>
          )}
          {files.length > 0 && <ActionButtons />}
        </Container>
      </Box>
      <AppFooter />
    </Box>
  )
}
